import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  RouterProvider,
} from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError, BASE_PATH } from './api.ts';
import { dropDataOnAccessChange } from './auth.tsx';
import './i18n/index.ts';
import { AppLayout } from './layout.tsx';
import { AREAS, itemsOf, sectionPath } from './navigation.ts';
import { WEB_MODULES } from './modules/index.ts';
import { loadAppearance, PlatformTheme, watchPlatformSettings } from './platform.tsx';
import { AuditPage } from './pages/Audit.tsx';
import { DepartmentHomePage, SectionHomePage } from './pages/DepartmentHome.tsx';
import { AccountPage } from './pages/Account.tsx';
import { HelpPage } from './pages/Help.tsx';
import { HomePage } from './pages/Home.tsx';
import { LoginPage } from './pages/Login.tsx';
import { PresentationPage } from './pages/Presentation.tsx';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordReset.tsx';
import { NotificationsPage } from './pages/Notifications.tsx';
import { RolesPage } from './pages/Roles.tsx';
import { DesignPage } from './pages/settings/Design.tsx';
import { PatternsPage } from './pages/settings/Patterns.tsx';
import { AiSettingsPage } from './pages/settings/Ai.tsx';
import { AiChatButton } from './components/AiChat.tsx';
import { PlatformPage } from './pages/settings/Platform.tsx';
import { StylePage } from './pages/settings/Style.tsx';
import { SettingsLayout } from './pages/settings/SettingsLayout.tsx';
import { SettingsOverviewPage } from './pages/settings/SettingsOverview.tsx';
import { UsersPage } from './pages/Users.tsx';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});
// Troca de sessão ou de permissões: descarta o que estava carregado.
dropDataOnAccessChange(queryClient);
// O tema sem o do zero acompanha o estilo gravado (ADR 0126).
watchPlatformSettings(queryClient);

// O botão do chat da IA fica por cima de todas as telas e só aparece para quem pode (ADR 0125).
const rootRoute = createRootRoute({
  component: () => (
    <>
      <PlatformTheme />
      <Outlet />
      <AiChatButton />
    </>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { error?: string; returnTo?: string } => ({
    error: typeof s.error === 'string' ? s.error : undefined,
    returnTo: typeof s.returnTo === 'string' ? s.returnTo : undefined,
  }),
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: ResetPasswordPage,
});

// A apresentação também abre por aqui para quem já entrou (no endereço raiz, só sem sessão).
const presentationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: () => <PresentationPage />,
});

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppLayout });
const parent = { getParentRoute: () => appRoute };

// Página de abertura de cada departamento do menu (ADR 0012).
const departmentRoutes = AREAS.filter((area) => area.id !== 'settings')
  .flatMap(itemsOf)
  .filter((item) => item.overview)
  .map((item) => createRoute({ ...parent, path: item.to, component: DepartmentHomePage }));
// Uma página por seção do menu: /<departamento>/<seção>.
const sectionRoutes = AREAS.flatMap((area) =>
  (area.sections ?? []).map((section) =>
    createRoute({ ...parent, path: sectionPath(area, section), component: SectionHomePage }),
  ),
);

// Configuração: o submenu (SettingsLayout) fica em volta das telas dela.
const settingsRoute = createRoute({ ...parent, path: '/settings', component: SettingsLayout });
const inSettings = { getParentRoute: () => settingsRoute };
const settingsRoutes = settingsRoute.addChildren([
  createRoute({ ...inSettings, path: '/', component: SettingsOverviewPage }),
  createRoute({ ...inSettings, path: '/platform', component: PlatformPage }),
  createRoute({ ...inSettings, path: '/style', component: StylePage }),
  createRoute({ ...inSettings, path: '/design', component: DesignPage }),
  createRoute({ ...inSettings, path: '/patterns', component: PatternsPage }),
  createRoute({ ...inSettings, path: '/ai', component: AiSettingsPage }),
  createRoute({ ...inSettings, path: '/users', component: UsersPage }),
  createRoute({ ...inSettings, path: '/groups', component: RolesPage }),
  createRoute({ ...inSettings, path: '/audit', component: AuditPage }),
]);

// As telas dos módulos instalados (ADR 0123), todas dentro da moldura logada.
const moduleRoutes = WEB_MODULES.flatMap((m) =>
  m.routes.map((r) => createRoute({ ...parent, path: r.path, component: r.component })),
);

/** Endereços antigos (links salvos e avisos já enviados) levam ao lugar novo. */
const MOVED: Record<string, string> = {
  '/system': '/settings',
  '/system/platform': '/settings',
  '/users': '/settings/users',
  '/roles': '/settings/groups',
  '/audit': '/settings/audit',
  '/my-access': '/account',
  '/guides': '/help',
  // Empresas, filiais e moedas saíram das telas (ADR 0115).
  '/legal-entities': '/',
  '/branches': '/',
  '/currencies': '/',
};
const movedRoutes = Object.entries(MOVED).map(([from, to]) =>
  createRoute({
    ...parent,
    path: from,
    beforeLoad: () => {
      throw redirect({ to, replace: true });
    },
  }),
);

// Módulo instalado: as rotas vêm de webModule.routes (ADR 0123), não daqui.
const routeTree = rootRoute.addChildren([
  loginRoute,
  presentationRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  appRoute.addChildren([
    createRoute({ ...parent, path: '/', component: HomePage }),
    ...departmentRoutes,
    ...sectionRoutes,
    ...moduleRoutes,
    settingsRoutes,
    ...movedRoutes,
    createRoute({ ...parent, path: '/account', component: AccountPage }),
    createRoute({ ...parent, path: '/help', component: HelpPage }),
    createRoute({ ...parent, path: '/notifications', component: NotificationsPage }),
  ]),
]);

// As telas podem morar num caminho (ex.: /meusistema), definido por APP_PUBLIC_URL.
const router = createRouter({
  routeTree,
  basepath: BASE_PATH || '/',
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// O visual gravado vale desde a primeira pintura, inclusive na tela de entrar (ADR 0135): a primeira
// tela só abre depois de ele chegar, para nunca aparecer o de fábrica e trocar logo depois.
void loadAppearance(queryClient).then(() =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  ),
);
